import { useRef } from 'react';

import { useIntl } from 'react-intl';

import { editorCardProvider } from '@atlaskit/editor-card-provider';
import { syncBlockMessages } from '@atlaskit/editor-common/messages';
import type { MentionNodeDataProvider } from '@atlaskit/editor-common/mention';
import { SYNCED_BLOCKS_DOCUMENTATION_URL } from '@atlaskit/editor-common/sync-block';
import { type PublicPluginAPI } from '@atlaskit/editor-common/types';
import { createDefaultPreset } from '@atlaskit/editor-core/preset-default';
import { usePreset } from '@atlaskit/editor-core/use-preset';
import type { AnalyticsPlugin } from '@atlaskit/editor-plugin-analytics/analyticsPluginType';
import {
	autocompletePlugin,
	type AutocompletePlugin,
	type AutocompletePluginOptions,
} from '@atlaskit/editor-plugin-autocomplete';
import { cardPlugin, type CardPlugin } from '@atlaskit/editor-plugin-card';
import { extensionPlugin, type ExtensionPlugin } from '@atlaskit/editor-plugin-extension';
import { type FocusPlugin } from '@atlaskit/editor-plugin-focus';
import { gridPlugin, type GridPlugin } from '@atlaskit/editor-plugin-grid';
import { type HyperlinkPlugin } from '@atlaskit/editor-plugin-hyperlink';
import { listPlugin, type ListPlugin } from '@atlaskit/editor-plugin-list';
import { type MaxContentSizePlugin } from '@atlaskit/editor-plugin-max-content-size';
import { mentionsPlugin, type MentionsPlugin } from '@atlaskit/editor-plugin-mentions';
import type { MentionsChangedHandler } from '@atlaskit/editor-plugin-mentions/types';
import { type PlaceholderPlugin } from '@atlaskit/editor-plugin-placeholder';
import type { QuickInsertPlugin } from '@atlaskit/editor-plugin-quick-insert';
import { saveOnEnterPlugin, type SaveOnEnterPlugin } from '@atlaskit/editor-plugin-save-on-enter';
import { textColorPlugin, type TextColorPlugin } from '@atlaskit/editor-plugin-text-color';
import { type TypeAheadPlugin } from '@atlaskit/editor-plugin-type-ahead';
import { type EditorView } from '@atlaskit/editor-prosemirror/view';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import type { LinkPickerProps, LinkPickerUiOptions } from '@atlaskit/link-picker/types';
import type { CardContext } from '@atlaskit/link-provider';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { useLinkPickerEditorProps } from '@atlassian/link-picker-plugins/editor';

import { arrowKeyPlugin, useArrowkeyPlugin, type UserMessage } from './arrow-key-plugin';
import {
	connectorSourceInputRulePlugin,
	type ConnectorSourceEntry,
} from './connector-source-input-rule';
import {
	creationInlineSuggestionPlugin,
	type InlineSuggestionConfig,
} from './creation-inline-suggestion-plugin';
import messages from './messages';
import { pasteTextColorPlugin } from './paste-text-color-plugin';

/**
 * The maximum number of characters that can be entered in the chat input.
 */
export const MAX_CHARACTERS_INPUT = 256000;

type EditorLinkPickerOptions = LinkPickerUiOptions &
	Pick<Partial<LinkPickerProps>, 'plugins'> & {
		shouldMergeAdditionalPlugins?: boolean;
	};

export type EditorPresetProps = {
	onSave: (view: EditorView) => void;
	productKey: string;
	siteId: string;
	arrowKeyPluginConfig: {
		userMessages: UserMessage[];
	};
	placeholderPrompts?: string[];
	placeholderPromptAnimationOptions?: {
		eraseDelay?: number;
		pauseBeforeErase?: number;
	};
	placeholderText?: string;
	inlineSuggestion?: InlineSuggestionConfig;
	/** Optional override for Link Picker options */
	linkPickerOptions?: EditorLinkPickerOptions;
	/**
	 * Typically used when the container is small (e.g., chromeless editor with small width).
	 */
	useShortMessages?: boolean;
	connectorSources?: ConnectorSourceEntry[];
	autocomplete?: AutocompletePluginOptions;
	/**
	 * When true, the @mention typeahead splits results into separate "People" and "Agents" sections.
	 */
	enableAgentSectioning?: boolean;
	handleMentionsChanged?: MentionsChangedHandler;
	/** Optional Smart Card context scoped to this editor instance. */
	smartCardContext?: CardContext;
	mentionNodeDataProvider?: MentionNodeDataProvider;
};

export const useEditorPreset = ({
	onSave,
	arrowKeyPluginConfig,
	productKey,
	siteId,
	placeholderPrompts,
	placeholderPromptAnimationOptions,
	placeholderText,
	inlineSuggestion,
	linkPickerOptions = {},
	useShortMessages = false,
	connectorSources = [],
	autocomplete,
	enableAgentSectioning = false,
	handleMentionsChanged,
	smartCardContext,
	mentionNodeDataProvider,
}: EditorPresetProps): {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	preset: any;
	/**
	 * This type needs to be defined explicitly otherwise we get deep import errors.
	 * This is a non-exhaustive list of plugins that are used in our preset - there are more default plugins but we want to avoid adding these to package.json if they are not used.
	 */
	editorApi?: PublicPluginAPI<
		[
			AnalyticsPlugin,
			CardPlugin,
			SaveOnEnterPlugin,
			MaxContentSizePlugin,
			MentionsPlugin,
			GridPlugin,
			QuickInsertPlugin,
			ListPlugin,
			TypeAheadPlugin,
			HyperlinkPlugin,
			FocusPlugin,
			PlaceholderPlugin,
			ExtensionPlugin,
			TextColorPlugin,
			AutocompletePlugin,
		]
	>;
} => {
	const { formatMessage } = useIntl();
	const linkPicker = useLinkPickerEditorProps(linkPickerOptions, {
		cloudId: siteId,
		product: productKey,
	});

	const { handleArrowDown, handleArrowUp, handleInputChange } = useArrowkeyPlugin({
		userMessages: arrowKeyPluginConfig.userMessages,
	});

	const knownSourcesRef = useRef<ConnectorSourceEntry[]>(connectorSources);
	knownSourcesRef.current = connectorSources;
	const handleMentionsChangedRef = useRef(handleMentionsChanged);
	handleMentionsChangedRef.current = handleMentionsChanged;
	const smartCardContextRef = useRef(smartCardContext);
	smartCardContextRef.current = smartCardContext;
	const forwardMentionsChangedRef = useRef<MentionsChangedHandler>((changes) => {
		handleMentionsChangedRef.current?.(changes);
	});
	const shouldForwardMentionChanges = handleMentionsChanged !== undefined;

	const autocompletePluginOptions = autocomplete
		? { ...autocomplete, surface: autocomplete.surface ?? 'rovo-chat' }
		: autocomplete;

	const { preset, editorApi } = usePreset(() => {
		const isUpdatedPlaceholderEnabled: () => boolean = () =>
			FeatureGates.getExperimentValue('rovo-chat-input-placeholder-updated', 'isEnabled', false);

		const placeholderConfig = placeholderPrompts
			? { placeholderPrompts, placeholderPromptAnimationOptions }
			: placeholderText
				? { placeholder: placeholderText }
				: {
						placeholder: formatMessage(
							useShortMessages
								? messages.placeholderShort
								: isUpdatedPlaceholderEnabled()
									? messages.placeholderUpdated
									: messages.placeholder,
						),
					};

		return createDefaultPreset({
			allowAnalyticsGASV3: fg('platform_rovo_chat_enable_editor_plugin_analytics'),
			placeholder: placeholderConfig,
			paste: {
				pasteWarningOptions: {
					cannotPasteSyncedBlock: {
						title: syncBlockMessages.cannotPasteSyncedBlockTitle,
						description: syncBlockMessages.cannotPasteSyncedBlockUnsupportedDescription,
						urlHref: SYNCED_BLOCKS_DOCUMENTATION_URL,
						urlText: syncBlockMessages.cannotPasteSyncedBlockAction,
					},
				},
			},
			hyperlinkOptions: {
				linkPicker,
				lpLinkPicker: true,
			},
			blockType: {
				allowBlockType: {
					exclude: ['heading'],
				},
				lastNodeMustBeParagraph: false,
			},
			toolbar: {
				enableNewToolbarExperience: false,
			},
		})
			.add([saveOnEnterPlugin, onSave])
			.add([
				mentionsPlugin,
				{
					enableAgentSectioning:
						enableAgentSectioning || isExperimentEnabled('rovo_chat_mention_agents'),
					handleMentionsChanged: shouldForwardMentionChanges
						? forwardMentionsChangedRef.current
						: undefined,
					...(mentionNodeDataProvider ? { mentionNodeDataProvider } : {}),
				},
			])
			.add(gridPlugin)
			.add([
				cardPlugin,
				{
					allowBlockCards: false,
					allowEmbeds: false,
					linkPicker,
					lpLinkPicker: true,
					provider: Promise.resolve(editorCardProvider),
					smartCardContext: smartCardContextRef.current,
				},
			])
			.add(listPlugin)
			.add(extensionPlugin)
			.add([connectorSourceInputRulePlugin, { knownSourcesRef }])
			.maybeAdd([creationInlineSuggestionPlugin, inlineSuggestion], !!inlineSuggestion)
			.add(textColorPlugin)
			.add(pasteTextColorPlugin)
			.maybeAdd(
				[autocompletePlugin, autocompletePluginOptions],
				// productKey gate before the experiment read keeps exposure Confluence-only.
				!!autocomplete &&
					productKey === 'confluence' &&
					FeatureGates.getExperimentValue(
						'platform_editor_ai_autocomplete_rovo_chat_editor',
						'isEnabled',
						false,
					),
			)
			.add([
				arrowKeyPlugin,
				{
					onArrowDown: handleArrowDown,
					onArrowUp: handleArrowUp,
					onInputChange: handleInputChange,
				},
			]);
	}, [inlineSuggestion, shouldForwardMentionChanges, mentionNodeDataProvider]);

	return {
		preset,
		editorApi,
	};
};
